import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import AlignVerticalTopIcon from "@mui/icons-material/AlignVerticalTop";
import AlignVerticalBottomIcon from "@mui/icons-material/AlignVerticalBottom";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import {ElevationApp} from "../../ElevationApp";
import {MdSelection} from "../../model/view/MdViewSettings";
import {RibbonButton, RibbonGroup} from "../Ribbon";
import {MenubarShortcut, useMenubarShortcuts} from "../useMenubarShortcuts";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {doorLiteGlass, MdGlass, withGlassEdit} from "../../model/glass/MdGlass";
import {anyBeadConflict, GlassControls, sharedGlass} from "./GlassControls";

type DoorLiteSel = Extract<MdSelection, {type: "door-lite"}>;

// the contextual tab for glass lites selected in the door-elevation view. A lite's
// dimensions are edited directly on the canvas; the tab offers the glass
// configuration, removal, and (for a multi-selection) align/distribute arrangement.
export function DoorLiteSelection(props: {app: ElevationApp; selection: DoorLiteSel[]}) {
    const app = props.app;
    const sel = props.selection;
    const multi = sel.length > 1;
    const ids = sel.map(s => s.id);

    // re-render on lite edits so the Glass group tracks the current values.
    useEventEmitter2(app.doorSpec.litesChanged);

    const lites = app.doorSpec.lites.filter(l => ids.includes(l.id));
    const glassList = lites.map(doorLiteGlass);

    // apply one glass edit to every selected lite; each lite runs the thermal rules
    // against its own record, and the spec spreads a bead change over the leaf.
    const editGlass = (patch: MdGlass) => {
        for(const lite of lites) {
            app.doorSpec.setLiteGlass(lite.id, withGlassEdit(lite.glass, patch));
        }
    };

    const remove = () => {
        for(const id of ids) app.doorSpec.removeLite(id);
        // back to the Door tab so "Add Lite" stays at hand.
        app.viewSettings.selection.emit([]);
        app.viewSettings.activeTab.emit("workspace");
    };

    // align works for one lite too: a single selection anchors it to the edge (locks that edge's
    // offset + the axis size), a multi-selection aligns the lites to a shared edge.
    const align = (edge: "left" | "right" | "top" | "bottom") => {
        if(ids.length === 0) return;
        app.doorSpec.alignLites(ids, edge);
    };

    const distribute = () => {
        if(!multi) return;
        app.doorSpec.distributeLites(ids);
    };

    // copy each selected lite onto its own leaf and carry the selection to the copies, so a
    // repeated press walks a row of lites across the leaf.
    const duplicate = () => {
        const copies = app.doorSpec.duplicateLites(ids);
        if(copies.length === 0) return;

        app.viewSettings.selection.emit(copies.map(c => ({type: "door-lite", id: c.id, leaf: c.leaf})));
    };

    const removeShortcut: MenubarShortcut = {
        keys: ["Delete", "Backspace"], display: "Delete",
        hint: multi ? "Press Delete to remove the lites" : "Press Delete to remove the lite",
        run: remove,
    };

    const duplicateShortcut: MenubarShortcut = {
        keys: ["d"], display: "D",
        hint: multi ? "Press D to duplicate the lites" : "Press D to duplicate the lite",
        run: duplicate,
    };

    useMenubarShortcuts([removeShortcut, duplicateShortcut]);

    return (
        <>
            <GlassControls
                app={app}
                glass={sharedGlass(glassList)}
                beadConflict={anyBeadConflict(glassList)}
                onChange={editGlass}
            />

            <RibbonGroup label="Actions">
                <RibbonButton
                    icon={<ContentCopyIcon />}
                    label="Duplicate"
                    shortcut={duplicateShortcut.display}
                    onClick={duplicateShortcut.run}
                />
                <RibbonButton
                    icon={<DeleteOutlineIcon sx={{color: "error.main"}} />}
                    label="Remove"
                    shortcut={removeShortcut.display}
                    onClick={removeShortcut.run}
                />
            </RibbonGroup>

            <RibbonGroup label="Arrange">
                <RibbonButton icon={<AlignHorizontalLeftIcon />} label="Align Left" onClick={() => align("left")} />
                <RibbonButton icon={<AlignHorizontalRightIcon />} label="Align Right" onClick={() => align("right")} />
                <RibbonButton icon={<AlignVerticalTopIcon />} label="Align Top" onClick={() => align("top")} />
                <RibbonButton icon={<AlignVerticalBottomIcon />} label="Align Bottom" onClick={() => align("bottom")} />
                <RibbonButton icon={<ViewWeekIcon />} label="Distribute" disabled={!multi} onClick={distribute} />
            </RibbonGroup>
        </>
    );
}
